import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { staticChemicalSuppliers } from "../config/constants";
import { ApiServiceErr, MutOptions, axiosApi } from "./apiService";

export interface DeleteChemicalTransportParams {
  supplierDataHandle: boolean;
  type: string;
  chemicalTransportId: string;
  chemicalId: string;
}

export interface PostChemicalDataParams {
  supplierDataHandle: boolean;
  type: string;
  processingId: string;
  chemicalData: any;
}

export interface PutChemicalDataParams {
  supplierDataHandle: boolean;
  type: string;
  chemicalId: string;
  chemicalData: any;
}
export interface DeleteChemicalDataParams {
  supplierDataHandle: boolean;
  type: string;
  chemicalData: {
    chemicalId: string;
    processingId: string;
  };
}

export interface PostChemicalTransportParams {
  supplierDataHandle: boolean;
  type: string;
  chemicalId: string;
  transportData: any;
}

export interface PutChemicalTransportParams {
  supplierDataHandle: boolean;
  type: string;
  chemicalTransportId: string;
  transportData: any;
}

export const useGetChemicalSuppliersList = (addNew: boolean = true) =>
  useQuery(
    ["getChemicalSuppliersList", addNew],
    async () => {
      const response = await axiosApi.get("/supplier/list");

      if (response.status === 200) {
        const newD = response.data.map((supplier: any) => ({
          id: supplier._id,
          title: supplier?.supplier_name,
          supplierDetails: supplier
        }));
        return addNew ? [...staticChemicalSuppliers, ...newD] : newD;
      }

      return response.data;
    },
    {
      refetchOnWindowFocus: false
    }
  );

export const useSupplierMap = () => {
  const { data: suppliers = [], isLoading, refetch } = useGetChemicalSuppliersList(false);

  const supplierMap = useMemo(() => {
    const map: Record<string, string> = {};
    suppliers.forEach((supplier: any) => {
      map[supplier.id] = supplier.title;
    });
    return map;
  }, [suppliers]);

  return {
    supplierMap,
    isLoading,
    refetch
  };
};

export const usePostChemicalSupplier = (opt?: MutOptions<string>) => {
  return useMutation<any, ApiServiceErr, any>(async data => {
    const response = await axiosApi.post("/supplier", data);
    return response.data;
  }, opt);
};

const buildPostChemicalUrl = (
  isSupplierData: boolean,
  gateType: string,
  processingId: string
): string => {
  const baseGate = isSupplierData
    ? gateType === "farm"
      ? "supplier-farm-gate"
      : "supplier-process-gate"
    : gateType === "farm"
    ? "farm-gate"
    : "process-gate";

  return `/${baseGate}/processing-chemical?processingId=${processingId}`;
};

export const usePostChemicalData = (opt?: MutOptions<string>) => {
  return useMutation<any, ApiServiceErr, PostChemicalDataParams>(async data => {
    const url = buildPostChemicalUrl(data.supplierDataHandle, data.type, data.processingId);
    return await axiosApi.post(url, data.chemicalData);
  }, opt);
};

const buildPutChemicalUrl = (
  isSupplierData: boolean,
  gateType: string,
  chemicalId: string
): string => {
  const baseGate = isSupplierData
    ? gateType === "farm"
      ? "supplier-farm-gate"
      : "supplier-process-gate"
    : gateType === "farm"
    ? "farm-gate"
    : "process-gate";

  return `/${baseGate}/processing-chemical/${chemicalId}`;
};

export const usePutChemicalData = (opt?: MutOptions<string>) => {
  return useMutation<any, ApiServiceErr, PutChemicalDataParams>(async data => {
    const url = buildPutChemicalUrl(data.supplierDataHandle, data.type, data.chemicalId);
    return await axiosApi.put(url, data.chemicalData);
  }, opt);
};

const buildDeleteChemicalUrl = (
  isSupplierData: boolean,
  gateType: string,
  chemicalId: string,
  processingId: string
): string => {
  const baseGate = isSupplierData
    ? gateType === "farm"
      ? "supplier-farm-gate"
      : "supplier-process-gate"
    : gateType === "farm"
    ? "farm-gate"
    : "process-gate";

  return `/${baseGate}/processing-chemical/${chemicalId}?processingId=${processingId}`;
};

export const useDeleteChemicalData = (opt?: MutOptions<string>) => {
  return useMutation<any, ApiServiceErr, DeleteChemicalDataParams>(async data => {
    const url = buildDeleteChemicalUrl(
      data.supplierDataHandle,
      data.type,
      data.chemicalData.chemicalId,
      data.chemicalData.processingId
    );
    return await axiosApi.delete(url);
  }, opt);
};

const buildPostChemicalTransportUrl = (
  isSupplierData: boolean,
  gateType: string,
  chemicalId: string
): string => {
  const baseGate = isSupplierData
    ? gateType === "farm"
      ? "supplier-farm-gate"
      : "supplier-process-gate"
    : gateType === "farm"
    ? "farm-gate"
    : "process-gate";

  return `/${baseGate}/processing-chemical-transport?chemicalId=${chemicalId}`;
};

export const usePostChemicalTransportData = (opt?: MutOptions<string>) => {
  return useMutation<any, ApiServiceErr, PostChemicalTransportParams>(async data => {
    const url = buildPostChemicalTransportUrl(data.supplierDataHandle, data.type, data.chemicalId);
    return await axiosApi.post(url, data.transportData);
  }, opt);
};

const buildPutChemicalTransportUrl = (
  isSupplierData: boolean,
  gateType: string,
  chemicalTransportId: string
): string => {
  const baseGate = isSupplierData
    ? gateType === "farm"
      ? "supplier-farm-gate"
      : "supplier-process-gate"
    : gateType === "farm"
    ? "farm-gate"
    : "process-gate";

  return `/${baseGate}/processing-chemical-transport/${chemicalTransportId}`;
};

export const usePutChemicalTransportData = (opt?: MutOptions<string>) => {
  return useMutation<any, ApiServiceErr, PutChemicalTransportParams>(async data => {
    const url = buildPutChemicalTransportUrl(
      data.supplierDataHandle,
      data.type,
      data.chemicalTransportId
    );
    return await axiosApi.put(url, data.transportData);
  }, opt);
};

const buildDeleteChemicalTransportUrl = (
  isSupplierData: boolean,
  gateType: string,
  chemicalTransportId: string,
  chemicalId: string
): string => {
  const baseGate = isSupplierData
    ? gateType === "farm"
      ? "supplier-farm-gate"
      : "supplier-process-gate"
    : gateType === "farm"
    ? "farm-gate"
    : "process-gate";

  return `/${baseGate}/processing-chemical-transport/${chemicalTransportId}?chemicalId=${chemicalId}`;
};

export const useDeleteChemicalTransportData = (opt?: MutOptions<string>) => {
  return useMutation<any, ApiServiceErr, DeleteChemicalTransportParams>(async data => {
    const url = buildDeleteChemicalTransportUrl(
      data.supplierDataHandle,
      data.type,
      data.chemicalTransportId,
      data.chemicalId
    );
    return await axiosApi.delete(url);
  }, opt);
};
